/**
 * `/settings/team` — member roster (M3.9 partial).
 *
 * Shows current members with roles. Invite flow is parked behind a
 * "Coming soon" badge until the Clerk Organizations vs custom-invite
 * decision lands — see issue #45.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTeamMembers } from "@/hooks/use-team";
import { formatTimestamp } from "@/lib/formatters";

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-purple-100 text-purple-800",
  admin: "bg-blue-100 text-blue-800",
  member: "bg-gray-100 text-gray-700",
};

export function SettingsTeamPage() {
  const members = useTeamMembers();

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View team members in your workspace.
          </p>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
          title="Invites are temporarily disabled while we evaluate Clerk Organizations support"
        >
          Invites · Coming soon
        </span>
      </div>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>
            {members.data ? `${members.data.items.length} member${members.data.items.length !== 1 ? "s" : ""}` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members.isPending ? (
            <div className="space-y-3">
              {(["a", "b", "c"] as const).map((k) => (
                <Skeleton key={k} className="h-12 w-full" />
              ))}
            </div>
          ) : members.data?.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet.</p>
          ) : (
            <div className="divide-y">
              {members.data?.items.map((m: import("@simpliwork/api-schema").TeamMember) => (
                <div key={m.user_id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-sm">{m.name ?? m.email}</p>
                    {m.name && <p className="text-xs text-muted-foreground">{m.email}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_COLORS[m.role] ?? "bg-gray-100 text-gray-700"}`}
                    >
                      {m.role}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      Joined {formatTimestamp(m.joined_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
